import { useMemo } from 'react';

import { Button, Container, Input, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { EventSensor } from 'components/events/EventSensor';
import api from 'utils/api';
import type { FetchedEvent } from './types';

const modalContainerStyle = {
  marginBottom: 48,
  marginTop: 48,
  border: '2px solid black',
  borderRadius: '20px',
  backgroundColor: 'white'
};

const labelMap: Record<string, string> = { locationFF: 'location' };

function DataField(props: { label: string; value: React.ReactNode }) {
  return (
    <div
      style={{
        minWidth: 300,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}
    >
      <span
        style={{
          fontSize: 16,
          fontWeight: 'bold'
        }}
      >
        {props.label
          // split pascalCase to pascal Case
          .replaceAll(/([a-z])([A-Z])/g, `$1 $2`)
          // Capitalise first letter
          .replace(/^\w/, (c) => c.toUpperCase())}
        :
      </span>
      <Input name={props.label} disabled value={props.value} />
    </div>
  );
}

export default function ViewEventModal(props: {
  rowData: FetchedEvent;
  handleCloseViewEventModal: () => void;
}) {
  const { data } = useQuery<
    Array<{ sensorClass: string; parameter: string; id: number }>
  >({
    queryKey: ['event-details', props.rowData.id],
    queryFn: () =>
      api
        .get(
          `/admin/eventDetails?${new URLSearchParams({ eventId: props.rowData.id.toString() }).toString()}`
        )
        .then((res) => res.data),
    staleTime: 0
  });

  const sensors = useMemo(() => {
    const paramDepthsGroupedBySensor: Record<string, Array<string>> = {};
    (data ?? []).forEach((sensor) => {
      if (!paramDepthsGroupedBySensor[sensor.sensorClass]) {
        paramDepthsGroupedBySensor[sensor.sensorClass] = [];
      }
      if (
        !paramDepthsGroupedBySensor[sensor.sensorClass].includes(
          sensor.parameter
        )
      ) {
        paramDepthsGroupedBySensor[sensor.sensorClass].push(sensor.parameter);
      }
    });
    return Object.entries(paramDepthsGroupedBySensor);
  }, [data]);

  return (
    <div
      style={{
        maxWidth: '100%',
        alignContent: 'center',
        border: 20,
        margin: 'auto',
        marginLeft: 150,
        marginRight: 150
      }}
    >
      <Container style={modalContainerStyle}>
        <div style={{ height: 40 }} />
        <Typography variant='h2' color='blue.900' align='center'>
          View Event
        </Typography>
        <div
          style={{
            display: `grid`,
            gridTemplateColumns: `repeat(2, minmax(0, 1fr))`,
            margin: '40px 150px',
            gap: 16,
            alignContent: 'start'
          }}
        >
          {Object.entries(props.rowData)
            .filter(
              // Remove null values as well as hidden values
              ([label, value]) =>
                value !== null &&
                ![
                  'isDeleted',
                  'createdByName',
                  'updatedByName',
                  'eventTypeId',
                  'buoyId'
                ].includes(label)
            )
            .map(([label, value]) => [
              labelMap[label] ? labelMap[label] : label,
              // Format date string
              ['startTs', 'endTs', 'createdAt', 'updatedAt'].includes(label)
                ? new Date(Number(value)).toLocaleString()
                : value
            ])
            .map(([label, value], i) => (
              <DataField label={label as string} key={i} value={value} />
            ))}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              gridColumn: `span 2 / span 2`
            }}
          >
            <span style={{ fontWeight: 'bold' }}>Sensors:</span>
            {sensors.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: '8px'
                }}
              >
                {Array(2)
                  .fill(0)
                  .map((_v, colIndex) => (
                    <div
                      key={colIndex}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        width: `calc(100%/${2})`,
                        flex: `1 1 0`
                      }}
                    >
                      {sensors
                        .filter((_v, i) => i % 2 === colIndex)
                        .map(([sensorClass, parameters], i) => (
                          <EventSensor
                            {...{ sensorClass, parameters }}
                            key={i}
                          />
                        ))}
                    </div>
                  ))}
              </div>
            )}
          </div>
          <div
            style={{
              marginTop: 40,
              gridColumn: `span 2 / span 2`
            }}
          >
            <Button
              variant='primary'
              color='dark'
              sx={{ marginLeft: 'auto' }}
              onClick={props.handleCloseViewEventModal}
            >
              Close
            </Button>
          </div>
        </div>
      </Container>
    </div>
  );
}
